import { Component, type ReactNode } from "react";
import { track } from "@/lib/analytics";
import { logMarketplaceEvent } from "@/lib/marketplaceEventLog";

// ─── Lazy panel error boundary (Launch Stability & Error Recovery V1) ──────
// Wraps a lazy-loaded drawer/modal/map so a chunk-load failure (stale asset
// after a deploy, offline mid-session) or an unexpected render error inside
// that ONE panel never takes down the rest of the page — everything outside
// this boundary (the marketplace list, filters, other cards) keeps working.
//
// "Cerrar" resets the boundary so a transient error gets a fresh render
// attempt the next time the panel opens. "Reintentar" reloads the page —
// the only reliable fix for a genuinely stale chunk reference, since
// React.lazy() caches the rejected import promise for the lifetime of the
// page and will keep throwing the same error on remount otherwise.

interface LazyPanelBoundaryProps {
  children: ReactNode;
  /** Called when the user dismisses the error (e.g. close the drawer). */
  onClose?: () => void;
}

interface LazyPanelBoundaryState {
  hasError: boolean;
}

export class LazyPanelBoundary extends Component<LazyPanelBoundaryProps, LazyPanelBoundaryState> {
  state: LazyPanelBoundaryState = { hasError: false };

  static getDerivedStateFromError(): LazyPanelBoundaryState {
    return { hasError: true };
  }

  componentDidCatch(error: unknown): void {
    // Never a title/description/query/booking_url — this is a JS error
    // message from a failed module load or render, not marketplace data.
    track("marketplace_panel_error", {
      error_message: error instanceof Error ? error.message.slice(0, 160) : "unknown",
    });
    // Admin Metrics UX V1 — same event (count only, never the message
    // text — marketplace_events has no error_message column at all).
    logMarketplaceEvent("marketplace_panel_error", {});
  }

  handleClose = (): void => {
    this.setState({ hasError: false });
    this.props.onClose?.();
  };

  handleReload = (): void => {
    window.location.reload();
  };

  render() {
    if (!this.state.hasError) return this.props.children;

    return (
      <div
        role="alert"
        className="p-4 rounded-xl text-center flex flex-col items-center gap-3"
        style={{ background: "oklch(0.993 0.004 72)", border: "1px solid oklch(0.880 0.024 72 / 0.35)" }}
      >
        <p className="text-[13px] font-semibold" style={{ color: "var(--color-heading-navy)" }}>
          No pudimos abrir este panel. Intenta de nuevo.
        </p>
        <div className="flex items-center gap-2">
          {this.props.onClose && (
            <button
              type="button"
              onClick={this.handleClose}
              className="text-[12px] font-semibold px-4 rounded-lg transition-opacity hover:opacity-80"
              style={{ minHeight: "40px", color: "oklch(0.545 0.020 60 / 0.75)", background: "oklch(0.545 0.020 60 / 0.08)" }}
            >
              Cerrar
            </button>
          )}
          <button
            type="button"
            onClick={this.handleReload}
            className="text-[12px] font-semibold px-4 rounded-lg transition-opacity hover:opacity-85"
            style={{ minHeight: "40px", color: "oklch(0.998 0.003 72)", background: "oklch(0.120 0.040 258)" }}
          >
            Reintentar
          </button>
        </div>
      </div>
    );
  }
}
